import { findIndex, get } from 'lodash';
import { DateTime } from 'luxon';
import { isDateInRange } from './date-utils';
import { areGroupConditionsMet } from './checklist-conditions';
import {
  type Checklist,
  type ChecklistStep,
  type Client,
  type ClientDeliverySheet,
  type WorkflowStatus,
  type ClientLinkType,
  type CustomField,
  type CustomFieldValues,
  type ClientGroup,
  type Appointment,
  type ClientDashboardClientAppointments,
  AppointmentClientGroupCondition,
} from '@/types';
import { useUserStore } from '@/stores/user';
import { getGeneralErrorMap, getNotificationDataByMap, type ErrorMapItem } from './error';
import { ErrorType } from '~/types/error';
import { areAllItemsMatching, hasOverlappingItems } from './array';

export const getStatusById = (
  statusId: WorkflowStatus['_id'],
  workflow: { statuses: WorkflowStatus[] }
) => {
  return workflow?.statuses?.find((find) => find._id === statusId);
};

export const customFieldValuesInjectionKey = Symbol() as InjectionKey<
  Ref<CustomFieldValues | undefined>
>;

export const updateClientInjectionKey = Symbol() as InjectionKey<{
  updateClientDeliverySheet: (
    client: Client,
    deliverySheet: ClientDeliverySheet | undefined
  ) => void;
  updateChecklistStepChecked: (
    client: Client,
    checklistId: Checklist['_id'],
    checklistStepId: ChecklistStep['_id'],
    checked: boolean
  ) => void;
  updateChecklistStepValue: (
    client: Client,
    checklistId: Checklist['_id'],
    checklistStepId: ChecklistStep['_id'],
    value: string
  ) => void;
  updateCustomFieldValue: (
    client: Client,
    customFieldId: CustomField['_id'],
    value: string | string[] | undefined
  ) => void;
  updateClientName: (client: Client, name: string) => void;
  updateClientGroups: (client: Client, clientGroups: ClientGroup[]) => void;
  updateClient: (client: Client, newClient: Client) => void;
}>;

export const checklistKey = 'checklists' as keyof Client;

export const isCurrentStatusCompleted = (
  client: Client,
  customFieldValues: CustomFieldValues
): boolean => {
  const workflowStatuses = client.inventory.workflow.statuses;
  const clientGroupIds = client.clientGroups.map((cg) => cg._id);

  const currentStatusIndex = findIndex(
    workflowStatuses,
    (status) => status._id === client.status
  );

  const currentChecklists = workflowStatuses[
    currentStatusIndex
  ].checklists.filter((c) =>
    areGroupConditionsMet(c.conditions, clientGroupIds, customFieldValues)
  );
  const nextChecklists = workflowStatuses[
    currentStatusIndex + 1
  ]?.checklists.filter((c) =>
    areGroupConditionsMet(c.conditions, clientGroupIds, customFieldValues)
  );

  if (!nextChecklists) {
    return true;
  }

  return (
    currentChecklists.length === 0 ||
    currentChecklists.every((checklist) => {
      if (
        checklist?.isPersistent &&
        nextChecklists.some(
          (nextChecklist) => nextChecklist?._id === checklist._id
        )
      ) {
        return true;
      }
      return (
        checklist.steps.length === 0 ||
        checklist.steps.every((step) =>
          get(client, [
            checklistKey,
            workflowStatuses[currentStatusIndex]._id,
            checklist._id,
            step._id ?? '',
            'value',
          ])
        )
      );
    })
  );
};

export const mapLinkedClients = <T extends { linkType: ClientLinkType }>(
  linkedClients: T[]
) => {
  const linkGroups: Record<string, T[]> = {};

  linkedClients.forEach((linkedClient: T) => {
    if (!linkGroups[linkedClient.linkType]) {
      linkGroups[linkedClient.linkType] = [];
    }

    linkGroups[linkedClient.linkType].push(linkedClient);
  });

  return linkGroups;
};

/**
 * @returns all checklists that
 * - are currently scheduled
 * - can be worked on because the client is in the same status
 * - are not completed yet
 * - the user's role is responsible for them
 */
export const getScheduledChecklists = (client: Client): Checklist[] => {
  const planningMethod = client.inventory.scheduledType;
  if (planningMethod) {
    const userStore = useUserStore();

    if (
      planningMethod === 'checklist' &&
      client.inventory.scheduledChecklists?.[client.status]
    ) {
      const scheduleForCurrentStatus =
        client.inventory.scheduledChecklists[client.status];
      const currentlyScheduledChecklistIds = Object.keys(
        scheduleForCurrentStatus
      ).filter((key) =>
        isDateInRange(
          new Date(),
          DateTime.fromISO(scheduleForCurrentStatus[key].startDate).toJSDate(),
          DateTime.fromISO(scheduleForCurrentStatus[key].endDate).toJSDate()
        )
      );

      const checklistsOfCurrentStatus = client.inventory.workflow.statuses.find(
        (status) => status._id === client.status
      )?.checklists;

      if (checklistsOfCurrentStatus) {
        return checklistsOfCurrentStatus.filter(
          (checklist) =>
            currentlyScheduledChecklistIds.includes(checklist._id) &&
            userStore.user &&
            checklist.responsibleRoles.includes(userStore.user.role) &&
            checklist.steps.length > 0 &&
            !checklist.steps.every((step) =>
              get(client, [
                checklistKey,
                client.status,
                checklist._id,
                step._id ?? '',
                'value',
              ])
            )
        );
      }
    } else if (
      planningMethod === 'clientGroup' &&
      client.inventory.scheduledClientGroups &&
      client.clientGroups.length
    ) {
      // The client group with the highest order determines the schedule of the client
      const highestClientGroup = client.clientGroups.sort(
        (a, b) => a.order - b.order
      )[0];

      const scheduledStatuses =
        client.inventory.scheduledClientGroups[highestClientGroup._id];

      if (scheduledStatuses) {
        const scheduleOfCurrentStatus = scheduledStatuses[client.status];

        if (
          scheduleOfCurrentStatus &&
          isDateInRange(
            new Date(),
            DateTime.fromISO(scheduleOfCurrentStatus.startDate).toJSDate(),
            DateTime.fromISO(scheduleOfCurrentStatus.endDate).toJSDate()
          )
        ) {
          const checklistsOfCurrentStatus =
            client.inventory.workflow.statuses.find(
              (status) => status._id === client.status
            )?.checklists;

          if (checklistsOfCurrentStatus) {
            return checklistsOfCurrentStatus.filter(
              (checklist) =>
                userStore.user &&
                checklist.responsibleRoles.includes(userStore.user.role) &&
                checklist.steps.length > 0 &&
                !checklist.steps.every((step) =>
                  get(client, [
                    checklistKey,
                    client.status,
                    checklist._id,
                    step._id ?? '',
                    'value',
                  ])
                )
            );
          }
        }
      }
    }
  }

  return [];
};

export const defaultFieldUpdateErrorHandler = (field: string, err: unknown, i18n: ReturnType<typeof useI18n>) => {
  if (err instanceof Error) {
    const errorMap: ErrorMapItem[] = [
      {
        type: ErrorType.NAME_ALREADY_EXISTS,
        message: i18n.t(
          'settings.client.error.client-name-already-exists-message'
        ),
        title: i18n.t('settings.client.error.client-name-already-exists-title'),
      },
      {
        type: ErrorType.NAME_IS_MANDATORY,
        message: i18n.t(
          'settings.client.error.client-name-is-mandatory-message'
        ),
        title: i18n.t('settings.client.error.client-name-is-mandatory-title'),
      },
      ...getGeneralErrorMap(i18n),
    ];

    const messageData = getNotificationDataByMap(i18n, errorMap, err, {
      title: i18n.t('general.' + field),
    });

    useErrorHandler(err, {
      type: 'error',
      title: messageData.title,
      message: messageData.message,
    });
  } else {
    useErrorHandler(err, {
      type: 'error',
      title: i18n.t('general.' + field),
      message: i18n.t('error-messages.something-went-wrong'),
    });
  }
}

export const getOpenAppointmentsByClient = (
  client: Client,
  appointments: Appointment[],
  doneClientAppointments: ClientDashboardClientAppointments,
) => {
  return appointments.filter((appointment) => {
    if (appointment.inventory?._id !== client?.inventory._id) {
      return false;
    }

    const isAppointmentDone = doneClientAppointments[
      client._id
    ]?.find(
      (clientAppointment) => clientAppointment.appointment === appointment._id
    );
    if (isAppointmentDone) {
      return false;
    }

    // Appointment is targeting every client
    if (!appointment.clientGroups.length) {
      return true;
    }

    // Client has no clientGroups and the appointment is clientGroup specific
    if (!client.clientGroups.length) {
      return false;
    }

    if (
      appointment.clientGroupCondition === AppointmentClientGroupCondition.OR
    ) {
      // Overlap 2 clientGroups
      return hasOverlappingItems(
        appointment.clientGroups,
        client.clientGroups ?? [],
        '_id',
        '_id'
      );
    }

    return areAllItemsMatching(
      appointment.clientGroups,
      client.clientGroups ?? [],
      '_id',
      '_id'
    );
  })
}